import {PopperCustomModifier} from '@blueprintjs/core';

// Breathing room between a popover and the edge of the window.
const VIEWPORT_MARGIN = 12;

// Set on the popover element by `publishPopoverMaxHeight`, for content that
// wants to fill the space the popover was given without outgrowing it.
export const POPOVER_MAX_HEIGHT_VAR = '--popoverMaxHeight';

export function availablePopoverHeight(
  triggerRect: {bottom: number; top: number},
  placement: string,
  viewportHeight: number,
  margin: number = VIEWPORT_MARGIN
): number {
  const opensUpward = placement.startsWith('top');
  const available = opensUpward
    ? triggerRect.top - margin
    : viewportHeight - triggerRect.bottom - margin;

  return Math.max(available, 0);
}

/**
 * Publishes the room between a popover's trigger and the window edge as
 * `--popoverMaxHeight` on the popover element.
 *
 * A CSS `max-height` can only measure the viewport, so a menu opened low on the
 * page still runs off the bottom. Only Popper knows where the trigger ended up
 * and which way the popover opened, and it recomputes on scroll and resize.
 *
 * Written straight to the element rather than through `state.styles`: Popper
 * applies those with `Object.assign(element.style, …)`, which silently drops
 * custom properties.
 */
export const publishPopoverMaxHeight: PopperCustomModifier = {
  name: 'publishPopoverMaxHeight',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: ({state}) => {
    const height = availablePopoverHeight(
      state.elements.reference.getBoundingClientRect(),
      state.placement,
      window.innerHeight
    );

    state.elements.popper.style.setProperty(POPOVER_MAX_HEIGHT_VAR, `${height}px`);
  },
};
